import React, { useContext } from "react";
import { Link, useNavigate } from "react-router";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation, Trans } from "react-i18next";
import { Grid2, Link as MuiLink, MenuItem } from "@mui/material";
import { MandatorContext } from "@bn/theming";
import {
  LoadingButton,
  RHFTextField,
  RHFCheckbox,
  RHFSelect,
  RHFPassword,
} from "@components";
import { useSignup, useLogin } from "@api/v1/hooks";
import { SignupErrorAlert } from "./SignupErrorAlert";

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

const LoadingButtonStyled = styled(LoadingButton)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

interface SignupFormFields {
  gender: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  acceptedAgb: boolean;
  receiveNewsletter: boolean;
}

export const SignupPageForm = () => {
  const { mandatorSlug } = useContext(MandatorContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const signupFormSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("form.errors.wrongEmailFormat"))
      .required(t("form.errors.required")),
    password: Yup.string().required(t("form.errors.required")),
    gender: Yup.number().oneOf([1, 2]).required(t("form.errors.required")),
    firstName: Yup.string().required(t("form.errors.required")),
    lastName: Yup.string().required(t("form.errors.required")),
    acceptedAgb: Yup.boolean()
      .required(t("form.errors.required"))
      .oneOf([true], t("signupPage.form.errors.agb")),
    receiveNewsletter: Yup.boolean().required(t("form.errors.required")),
  });

  const { handleSubmit, control } = useForm<SignupFormFields>({
    resolver: yupResolver(signupFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      gender: 1,
      acceptedAgb: false,
      receiveNewsletter: false,
    },
  });

  const {
    mutateAsync: createUser,
    isPending: isSignupLoading,
    error,
  } = useSignup();
  const { loginAsync: loginUser, isFetching: isLoginLoading } = useLogin();

  const onSubmit: SubmitHandler<SignupFormFields> = async (fields) => {
    await createUser({
      ...fields,
      mandatorPublicId: mandatorSlug || "rentablo",
    });
    await loginUser({
      grant_type: "password",
      username: fields.email,
      password: fields.password,
    });

    navigate("/confirmation/email");
  };

  const isNewsLetterCheckboxDisplayed = true;

  return (
    <FormStyled onSubmit={handleSubmit(onSubmit)} data-testid="signup-form">
      <SignupErrorAlert error={error} />
      <RHFSelect
        data-testid="signup-form-select"
        name="gender"
        label={t("signupPage.form.gender.title")}
        control={control}
      >
        <MenuItem value={1}>{t("signupPage.form.gender.male")}</MenuItem>
        <MenuItem value={2}>{t("signupPage.form.gender.female")}</MenuItem>
      </RHFSelect>
      <Grid2 container spacing={3}>
        <Grid2
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <RHFTextField
            fullWidth
            data-testid="signup-form-firstname"
            name="firstName"
            variant="outlined"
            size="small"
            label={t("signupPage.form.firstName")}
            control={control}
            inputProps={{ "data-testid": "signup-form-firstname-input" }}
          />
        </Grid2>

        <Grid2
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <RHFTextField
            fullWidth
            data-testid="signup-form-lastname"
            name="lastName"
            variant="outlined"
            size="small"
            label={t("signupPage.form.lastName")}
            control={control}
            inputProps={{ "data-testid": "signup-form-lastname-input" }}
          />
        </Grid2>
      </Grid2>
      <RHFTextField
        fullWidth
        data-testid="signup-form-email"
        name="email"
        type="email"
        variant="outlined"
        size="small"
        label={t("signupPage.form.email")}
        control={control}
        inputProps={{ "data-testid": "signup-form-email-input" }}
      />
      <RHFPassword
        fullWidth
        data-testid="signup-form-password"
        name="password"
        label={t("signupPage.form.password")}
        control={control}
        inputProps={{ "data-testid": "signup-form-password-input" }}
        showPasswordStrength
      />
      <CheckboxContainer>
        <RHFCheckbox
          name="acceptedAgb"
          data-testid="signup-form-agb-checkbox"
          control={control}
          label={
            <Trans
              i18nKey="signupPage.form.acceptedAgb"
              components={{
                agb: (
                  <MuiLink
                    component={Link}
                    to="/agb"
                    data-testid="signup-form-agb-link-agb"
                    underline="hover"
                  />
                ),
                privacy: (
                  <MuiLink
                    component={Link}
                    to="/privacy"
                    data-testid="signup-form-agb-link-privacy"
                    underline="hover"
                  />
                ),
              }}
            />
          }
        />

        {isNewsLetterCheckboxDisplayed && (
          <RHFCheckbox
            data-testid="signup-form-mailing-checkbox"
            name="receiveNewsletter"
            control={control}
            label={t("signupPage.form.news")}
          />
        )}
      </CheckboxContainer>
      <LoadingButtonStyled
        fullWidth
        data-testid="signup-form-submit"
        type="submit"
        variant="primary"
        isFetching={isSignupLoading || isLoginLoading}
      >
        {t("signupPage.form.submitButton")}
      </LoadingButtonStyled>
    </FormStyled>
  );
};
